import React from "react";
import { NavLink } from 'react-router-dom'
import { CCard, CCardHeader, CCardBody } from '@coreui/react'
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { codeSnippet } from '../../utils/codeSnippet/springBoot/Security';

function Security () {

    return (
      <CCard className="mb-4">
			  <CCardHeader><strong>Security</strong></CCardHeader>
        <CCardBody>
          <CCard>
            <CCardBody>
            </CCardBody>
          </CCard><br/>
          <p><strong>Core Concepts (must-know)</strong></p>
          <ul>
            <li>
              <p><b>Authentication</b></p>
              <ul>
                <li><p>Username and password</p></li>
                <li><p>JWT token</p></li>
                <li><p>OAuth login (Google, Okta, etc.)</p></li>
              </ul>
              <p>After successful authentication, Spring Security creates an Authentication object and stores it in the SecurityContext.</p>
              <p>This object contains: Username (principal), Roles/authorities, Authentication status</p>
            
            </li>
            <li>
              <p><b>Authorization</b></p>
              <p>It checks what the authenticated user is allowed to access.</p>
              <p>ROLE_USER, ROLE_ADMIN</p>
            
            </li>
            <li>
              <p><b>Security Filter Chain</b></p>
              <p>Every request goes through multiple filters before reaching the controller.</p>
              <p>Intercept request, Check authentication, Validate token/session, Check authorization</p>
              <p>If everything is valid → request reaches controller., If not → request blocked.</p>
            
            </li>            
          </ul>
          <strong>Authorization Code Grant with SSO in Spring Boot</strong>
          <p>This is the most common way to do SSO with Spring Boot.</p>
          <ul>
            <li><p>OAuth 2.0 Authorization Code Grant + OpenID Connect (OIDC)</p></li>
            <li><p>Works with a Central SSO Identity Provider (IdP) like Okta, Azure AD, Google, or a company internal IdP.</p></li>
            <li><p>Spring Boot does NOT handle passwords. The login happens at the IdP.</p></li>
          </ul>

          <p><b>Flow Steps</b></p>

          <ul>
            <li><p>User accesses Spring Boot app: The user opens a protected URL in the Spring Boot application.</p></li>
            <li><p>Spring Boot redirects to SSO (Authorization Endpoint): Spring Security automatically redirects the browser to the SSO authorization endpoint.</p>
              <p><i><b>https://sso.company.com/oauth2/authorize</b></i></p>
              <p>Request parameters include:</p>
              <ul>
                <li><p>client_id</p></li>
                <li><p>redirect_uri</p></li>
                <li><p>scope=openid profile email</p></li>
                <li><p>response_type=code</p></li>
              </ul>
            </li>
            <li><p>User authenticates at SSO: The SSO provider handles authentication:</p></li>
            <li><p>Authentication happens only at the SSO server</p></li>
            <li><p>SSO issues Authorization Code</p>
              <p>After successful login, SSO redirects the browser back to the application.</p>
              <p><i><b>https://app.company.com/login/oauth2/code/company?code=abc123 </b></i>(Single-use, Cannot access APIs)</p>
            </li>
            <li><p>Spring Boot exchanges code for tokens (Backend): The backend makes a server-to-server call to the token endpoint.</p>
            <p><i><b>https://sso.company.com/oauth2/token</b></i></p>
            <p><b>Spring Boot sends:</b></p>
            <ul>
              <li><p>Authorization code</p></li>
              <li><p>Client ID</p></li>
              <li><p>Client secret</p></li>
            </ul>
            <p><b>SSO returns:</b></p>
            <ul>
              <li><p>ID Token (JWT) – identifies the user</p></li>
              <li><p>Access Token – used to call APIs</p></li>
              <li><p>(Optional) Refresh Token</p></li>
            </ul>
            </li>
          </ul>
          <p>Spring Boot Configuration (application.yml)</p>
        <SyntaxHighlighter wrapLines="true" language="javascript" style={docco}>{codeSnippet.code1}</SyntaxHighlighter>
        <SyntaxHighlighter wrapLines="true" language="javascript" style={docco}>{codeSnippet.code2}</SyntaxHighlighter>

        <p><b>Method-level Authorization with @PreAuthorize</b></p>
        <SyntaxHighlighter wrapLines="true" language="javascript" style={docco}>{codeSnippet.code3}</SyntaxHighlighter>

        <p><b>Accessing User Details in Controller</b></p>
        <SyntaxHighlighter wrapLines="true" language="javascript" style={docco}>{codeSnippet.code4}</SyntaxHighlighter>
        <SyntaxHighlighter wrapLines="true" language="javascript" style={docco}>{codeSnippet.code5}</SyntaxHighlighter>
        <SyntaxHighlighter wrapLines="true" language="javascript" style={docco}>{codeSnippet.code6}</SyntaxHighlighter>
        <p>From anywhere (Service layer also)</p>
        <SyntaxHighlighter wrapLines="true" language="javascript" style={docco}>{codeSnippet.code7}</SyntaxHighlighter>


{/* <br/><br/><br/><br/><br/>

  <ul>
    <li>🔐 <strong>Authentication</strong> → Who are you?</li>
    <li>🛡 <strong>Authorization</strong> → What are you allowed to do?</li>
  </ul>

  <p><strong>Core Concepts (must-know)</strong></p>

  <p><strong>1️⃣ Authentication</strong></p>
  <p>Verifying user identity</p>
  <p>Examples:</p>
  <ul>
    <li>Username / password</li>
    <li>JWT token</li>
    <li>OAuth (Google, GitHub)</li>
  </ul>

  <p><strong>2️⃣ Authorization</strong></p>
  <p>Checking permissions</p>
  <p>Examples:</p>
  <ul>
    <li>ROLE_USER</li>
    <li>ROLE_ADMIN</li>
  </ul>

  <p><strong>3️⃣ Security Filter Chain</strong></p>
  <p>A chain of filters that:</p>
  <ul>
    <li>Intercept requests</li>
    <li>Authenticate users</li>
    <li>Check access rules</li>
  </ul>
  <p>✅ Use <strong>SecurityFilterChain</strong></p>

  <p><strong>Basic example:</strong></p>
  <pre>
{`@Bean
SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception {
  http
    .csrf(csrf -> csrf.disable())
    .authorizeHttpRequests(auth -> auth
      .requestMatchers("/public/**").permitAll()
      .anyRequest().authenticated()
    )
    .httpBasic();
  return http.build();
}`}
  </pre>

  <p><strong>🔐 Authorization Code Grant with SSO — Spring Boot</strong></p>
  <p><strong>OAuth 2.0 Authorization Code Grant</strong> with <strong>OpenID Connect (OIDC)</strong> for login.</p>
  <p>Uses a <strong>Central SSO Identity Provider</strong> such as Okta, Azure AD, Google, or an internal IdP.</p>
  <p><strong>Important:</strong> The Spring Boot application <strong>does NOT handle passwords</strong>.</p>
  <p>The app redirects the user to SSO login, receives an authorization code, exchanges it for tokens, validates the ID token, and creates a logged-in session.</p>

  <p><strong>Flow Steps:</strong></p>
  <ul>
    <li><strong>1️⃣ User accesses Spring Boot app</strong><br />The user opens a protected URL in the Spring Boot application.</li>
    <li><strong>2️⃣ Spring Boot redirects to SSO (Authorization Endpoint)</strong><br />Spring Security automatically redirects the browser to the SSO authorization endpoint.</li>
  </ul>
  <pre>https://sso.company.com/oauth2/authorize</pre>
  <p>Request parameters include:</p>
  <ul>
    <li>client_id</li>
    <li>redirect_uri</li>
    <li>scope=openid profile email</li>
    <li>response_type=code</li>
  </ul>
  <p><strong>📌 No credentials are sent to Spring Boot</strong></p>

  <ul>
    <li><strong>3️⃣ User authenticates at SSO</strong><br />The SSO provider handles authentication:</li>
  </ul>
  <ul>
    <li>Username and password</li>
    <li>MFA</li>
    <li>Device and risk checks</li>
  </ul>
  <p><strong>✔️ Authentication happens only at the SSO server</strong></p>

  <ul>
    <li><strong>4️⃣ SSO issues Authorization Code</strong><br />After successful login, SSO redirects the browser back to the application.</li>
  </ul>
  <pre>https://app.company.com/login/oauth2/code/company?code=abc123</pre>
  <p>Authorization code characteristics:</p>
  <ul>
    <li>Short-lived</li>
    <li>Single-use</li>
    <li>Cannot access APIs</li>
  </ul>

  <ul>
    <li><strong>5️⃣ Spring Boot exchanges code for tokens (Backend)</strong><br />The backend makes a <strong>server-to-server</strong> call to the token endpoint.</li>
  </ul>
  <pre>POST /oauth2/token</pre>
  <p>Spring Boot sends:</p>
  <ul>
    <li>Authorization code</li>
    <li>Client ID</li>
    <li>Client secret</li>
  </ul>
  <p>SSO returns:</p>
  <ul>
    <li><strong>ID Token (JWT)</strong> – identifies the user</li>
    <li><strong>Access Token</strong> – used to call APIs</li>
    <li><strong>(Optional) Refresh Token</strong></li>
  </ul>

  <ul>
    <li><strong>6️⃣ Spring Security validates tokens</strong></li>
    <li>Verifies token signature, issuer, audience, and expiry</li>
    <li><strong>7️⃣ User session is created</strong></li>
  </ul>
  <p>Spring Boot:</p>
  <ul>
    <li>Stores authentication in <code>SecurityContext</code></li>
    <li>Creates an HTTP session</li>
  </ul>
  <p><strong>✅ User is now logged in</strong></p>

  <p><strong>Visual Flow (Simplified)</strong></p>
  <pre>
Browser → Authorization Endpoint → Authorization Code App Backend → Token Endpoint → JWT Tokens
  </pre>

  <p><strong>Spring Boot Configuration (application.yml)</strong></p>
  <pre>
{`
spring:
  security:
    oauth2:
      client:
        registration:
          company:
            client-id: app-client-id
            client-secret: app-client-secret
            scope: openid,profile,email
        provider:
          company:
            issuer-uri: https://sso.company.com
`}
  </pre>

  <pre>
{`
@Bean SecurityFilterChain securityFilterChain(HttpSecurity http) throws Exception { 
  http 
    .authorizeHttpRequests(auth -> auth 
      .requestMatchers("/public/**").permitAll() 
      .anyRequest().authenticated() ) 
    .oauth2Login(); // Authorization Code + SSO 
    
  return http.build(); 
}`}
  </pre>

  <strong>2️⃣ Stores authentication in SecurityContext</strong>
  <p>Spring Security uses a <code>SecurityContext</code> object to keep track of the current authenticated user. 
  <code>SecurityContext</code> contains an <code>Authentication</code> object.</p>

  <p><strong>Authentication holds:</strong></p>
  <ul>
    <li><strong>Principal:</strong> the user details (username, email, roles, etc.)</li>
    <li><strong>Authorities:</strong> roles/permissions</li>
    <li><strong>Authentication status:</strong> authenticated or not</li>
  </ul>

  <p><strong>Example (conceptually):</strong></p>
  <pre>
Authentication auth = SecurityContextHolder.getContext().getAuthentication();
String username = auth.getName();
  </pre>

  <p>Spring automatically populates the <code>SecurityContext</code> after a successful login.</p>
  <p>This allows you to call <code>@AuthenticationPrincipal</code> in controllers or use <code>SecurityContextHolder.getContext()</code> anywhere.</p>

  <strong>1️⃣ Angular sends the JWT</strong>
  <p>Spring Security intercepts the request using a filter that runs before the request reaches controllers.</p>

  <p><strong>Usually a JWT authentication filter:</strong></p>
  <pre>
{`
public class JwtAuthenticationFilter extends OncePerRequestFilter {
    @Override
    protected void doFilterInternal(HttpServletRequest request,
                                    HttpServletResponse response,
                                    FilterChain filterChain) throws ServletException, IOException {
        String authHeader = request.getHeader("Authorization");
        if (authHeader != null && authHeader.startsWith("Bearer ")) {
            String token = authHeader.substring(7);
            // validate token and set Authentication
        }
        filterChain.doFilter(request, response);
    }
}`}
  </pre>

  <pre>
{`
@GetMapping("/user")
public String getUser(@AuthenticationPrincipal UserDetails user) {
    return user.getUsername();
}`}
  </pre>

  <pre>
{`
@EnableWebSecurity
public class SecurityConfig extends WebSecurityConfigurerAdapter {

    @Override
    protected void configure(HttpSecurity http) throws Exception {
        http
            .csrf().disable()
            .authorizeRequests()
                .antMatchers("/auth/**").permitAll()
                .anyRequest().authenticated()
            .and()
            .sessionManagement().sessionCreationPolicy(SessionCreationPolicy.STATELESS)
            .and()
            .addFilterBefore(new JwtAuthenticationFilter(), UsernamePasswordAuthenticationFilter.class);
    }
}`}
  </pre>


  <p><strong>Spring Security Authorization Example</strong></p>

  <p><strong>1️⃣ Security Configuration (WebSecurityConfigurerAdapter)</strong></p>
  <pre>
{`@Configuration
@EnableWebSecurity
public class SecurityConfig extends WebSecurityConfigurerAdapter {

    @Override
    protected void configure(HttpSecurity http) throws Exception {
        http
            .authorizeRequests()
                .antMatchers("/admin/**").hasRole("ADMIN")
                .antMatchers("/user/**").hasAnyRole("USER", "ADMIN")
                .anyRequest().authenticated()
            .and()
            .formLogin(); // or httpBasic() or oauth2Login()
    }
}`}
  </pre>

  <p><strong>2️⃣ Method-level Authorization with @PreAuthorize</strong></p>
  <pre>
{`@PreAuthorize("hasRole('ADMIN')")
@GetMapping("/admin/dashboard")
public String getAdminDashboard() {
    return "Secret Admin Data";
}`}
  </pre>

  <p><strong>Explanation:</strong></p>
  <ul>
    <li><strong>antMatchers("/admin/**").hasRole("ADMIN")</strong> → Only users with ROLE_ADMIN can access admin URLs.</li>
    <li><strong>antMatchers("/user/**").hasAnyRole("USER", "ADMIN")</strong> → Users with ROLE_USER or ROLE_ADMIN can access user URLs.</li>
    <li><strong>anyRequest().authenticated()</strong> → All other URLs require authentication, no specific role.</li>
    <li><strong>@PreAuthorize("hasRole('ADMIN')")</strong> → Secures the method so only ADMINs can call it.</li>
  </ul>


  <p><strong>Spring Security Authorization Example</strong></p>

  <p><strong>1️⃣ Security Configuration (WebSecurityConfigurerAdapter)</strong></p>
  <pre>
{`@Configuration
@EnableWebSecurity
public class SecurityConfig extends WebSecurityConfigurerAdapter {

    @Override
    protected void configure(HttpSecurity http) throws Exception {
        http
            .authorizeRequests()
                .antMatchers("/admin/**").hasRole("ADMIN")
                .antMatchers("/user/**").hasAnyRole("USER", "ADMIN")
                .anyRequest().authenticated()
            .and()
            .formLogin(); // or httpBasic() or oauth2Login()
    }
}`}
  </pre>

  <p><strong>2️⃣ Method-level Authorization with @PreAuthorize</strong></p>
  <pre>
{`@PreAuthorize("hasRole('ADMIN')")
@GetMapping("/admin/dashboard")
public String getAdminDashboard() {
    return "Secret Admin Data";
}`}
  </pre>

  <p><strong>Explanation:</strong></p>
  <ul>
    <li><strong>antMatchers("/admin/**").hasRole("ADMIN")</strong> → Only users with ROLE_ADMIN can access admin URLs.</li>
    <li><strong>antMatchers("/user/**").hasAnyRole("USER", "ADMIN")</strong> → Users with ROLE_USER or ROLE_ADMIN can access user URLs.</li>
    <li><strong>anyRequest().authenticated()</strong> → All other URLs require authentication, no specific role.</li>
    <li><strong>@PreAuthorize("hasRole('ADMIN')")</strong> → Secures the method so only ADMINs can call it.</li>
  </ul>
 */}


      
        </CCardBody>
      </CCard>        
    )
}

export default Security